import { mergeAttributes, Node } from "@tiptap/core"

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		color: {
			insertColorSwatches: (variant?: ColorVariant) => ReturnType
			updateColorItem: (
				pos: number,
				attrs: Partial<{ name: string; value: string; dark: string | null }>,
			) => ReturnType
			addColorItem: (pos: number) => ReturnType
			removeColorItem: (pos: number) => ReturnType
			addColorRow: (pos: number) => ReturnType
		}
	}
}

export type ColorVariant = "compact" | "table"

export const clampColorVariant = (value: unknown): ColorVariant =>
	value === "table" ? "table" : "compact"

// The value goes into a style attribute, so only what a CSS colour can hold
// gets through: no `;`, quotes, `url(` or braces.
export function sanitizeColorValue(value: unknown): string {
	const text = String(value ?? "").trim()
	if (!text || text.length > 64) return ""
	if (!/^[#a-zA-Z0-9(),.%\s/+-]+$/.test(text)) return ""
	if (/url\s*\(/i.test(text)) return ""
	return text
}

const DEFAULT_ITEMS = [
	{ name: "blue-500", value: "#3B82F6" },
	{ name: "blue-600", value: "#2563EB" },
	{ name: "blue-700", value: "#1D4ED8" },
]

const itemContent = () =>
	DEFAULT_ITEMS.map((attrs) => ({ type: "colorItem", attrs }))

/** One swatch. An atom: its name and value are attributes, edited in a node view. */
export const ColorItemExtension = Node.create({
	name: "colorItem",
	atom: true,
	selectable: true,
	draggable: false,

	addAttributes() {
		return {
			name: {
				default: "",
				parseHTML: (element: HTMLElement) =>
					element.getAttribute("data-name") ?? "",
			},
			value: {
				default: "",
				parseHTML: (element: HTMLElement) =>
					sanitizeColorValue(element.getAttribute("data-value")),
			},
			dark: {
				default: null,
				parseHTML: (element: HTMLElement) =>
					sanitizeColorValue(element.getAttribute("data-dark")) || null,
			},
		}
	},

	parseHTML() {
		return [{ tag: 'div[data-type="color-item"]' }]
	},

	renderHTML({ node }) {
		const name = String(node.attrs.name ?? "")
		const light = sanitizeColorValue(node.attrs.value)
		const dark = sanitizeColorValue(node.attrs.dark) || null
		const shownDark = dark ?? light
		const style = `--swatch-light: ${light || "transparent"}; --swatch-dark: ${
			shownDark || "transparent"
		}`
		const label = dark && dark !== light ? `${light} / ${dark}` : light
		return [
			"div",
			{
				"data-type": "color-item",
				class: "pl-color-item",
				"data-name": name,
				"data-value": light,
				...(dark ? { "data-dark": dark } : {}),
				style,
			},
			[
				"button",
				{
					type: "button",
					class: "pl-color-swatch",
					"data-color-copy": "",
					"aria-label": name || light,
					"aria-pressed": "false",
					"aria-roledescription": "Copy",
					title: name ? `${name}: ${label}` : label,
				},
				["span", { class: "pl-color-check", "aria-hidden": "true" }],
			],
			["span", { role: "status", class: "pl-color-status" }],
			[
				"div",
				{ class: "pl-color-meta" },
				["p", { class: "pl-color-name", title: name }, name],
				[
					"p",
					{ class: "pl-color-value", title: light },
					["span", { class: "pl-color-value-light" }, light],
					["span", { class: "pl-color-value-dark" }, shownDark],
				],
			],
		]
	},

	addCommands() {
		return {
			updateColorItem:
				(pos, attrs) =>
				({ tr, state, dispatch }) => {
					const node = state.doc.nodeAt(pos)
					if (node?.type.name !== this.name) return false
					if (dispatch) {
						const next = { ...node.attrs, ...attrs }
						tr.setNodeMarkup(pos, undefined, {
							name: String(next.name ?? ""),
							value: String(next.value ?? ""),
							dark: next.dark ? String(next.dark) : null,
						})
					}
					return true
				},

			addColorItem:
				(pos) =>
				({ tr, state, dispatch }) => {
					const node = state.doc.nodeAt(pos)
					if (node?.type.name !== this.name) return false
					if (dispatch) {
						tr.insert(pos + node.nodeSize, node.type.create({ ...node.attrs }))
					}
					return true
				},

			removeColorItem:
				(pos) =>
				({ tr, state, dispatch }) => {
					const node = state.doc.nodeAt(pos)
					if (node?.type.name !== this.name) return false
					if (!dispatch) return true
					const $pos = state.doc.resolve(pos)
					// The last swatch takes its row with it, and the last row its palette.
					let target: number | null = null
					for (let depth = $pos.depth; depth > 0; depth -= 1) {
						const parent = $pos.node(depth)
						const swatches =
							parent.type.name === "colorRow"
								? parent.childCount - 1
								: parent.childCount
						if (swatches > 1) break
						target = depth
						if (parent.type.name === "color") break
					}
					if (target === null) {
						tr.delete(pos, pos + node.nodeSize)
					} else {
						tr.delete($pos.before(target), $pos.after(target))
					}
					return true
				},

			addColorRow:
				(pos) =>
				({ tr, state, dispatch }) => {
					const $pos = state.doc.resolve(pos)
					let depth = $pos.depth
					while (depth > 0 && $pos.node(depth).type.name !== "colorRow")
						depth -= 1
					if (depth === 0) return false
					if (dispatch) {
						const { schema } = state
						const row = schema.nodes.colorRow?.create(null, [
							schema.nodes.colorRowTitle!.create(),
							schema.nodes.colorItem!.create({ ...DEFAULT_ITEMS[0] }),
						])
						if (row) tr.insert($pos.after(depth), row)
					}
					return true
				},
		}
	},
})

export const ColorRowTitleExtension = Node.create({
	name: "colorRowTitle",
	content: "inline*",
	marks: "_",
	defining: true,

	parseHTML() {
		return [{ tag: "p[data-color-row-title]" }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"p",
			mergeAttributes(HTMLAttributes, {
				"data-color-row-title": "",
				class: "pl-color-row-title",
			}),
			0,
		]
	},
})

/** Mintlify's Color.Row: a labelled line of swatches in the table variant. */
export const ColorRowExtension = Node.create({
	name: "colorRow",
	content: "colorRowTitle colorItem+",
	defining: true,
	isolating: true,

	parseHTML() {
		return [{ tag: 'div[data-type="color-row"]' }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"div",
			mergeAttributes(HTMLAttributes, {
				"data-type": "color-row",
				class: "pl-color-row",
			}),
			0,
		]
	},
})

export const ColorExtension = Node.create({
	name: "color",
	group: "block",
	content: "colorRow+ | colorItem+",
	defining: true,
	isolating: true,

	addAttributes() {
		return {
			variant: {
				default: "compact",
				parseHTML: (element: HTMLElement) =>
					clampColorVariant(element.getAttribute("data-variant")),
				renderHTML: (attributes: Record<string, unknown>) => ({
					"data-variant": clampColorVariant(attributes.variant),
				}),
			},
		}
	},

	parseHTML() {
		return [{ tag: 'div[data-type="color"]' }]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"div",
			mergeAttributes(HTMLAttributes, {
				"data-type": "color",
				class: "pl-color",
			}),
			0,
		]
	},

	addCommands() {
		return {
			insertColorSwatches:
				(variant = "compact") =>
				({ commands }) =>
					commands.insertContent(
						variant === "table"
							? {
									type: this.name,
									attrs: { variant: "table" },
									content: [
										{
											type: "colorRow",
											content: [
												{
													type: "colorRowTitle",
													content: [{ type: "text", text: "Primary" }],
												},
												...itemContent(),
											],
										},
									],
								}
							: {
									type: this.name,
									attrs: { variant: "compact" },
									content: itemContent(),
								},
					),
		}
	},
})

export const ColorExtensions = [
	ColorExtension,
	ColorRowExtension,
	ColorRowTitleExtension,
	ColorItemExtension,
]
